import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useFeatureFlags, FEATURE_FLAGS } from './FeatureFlagContext';

// Context
const GlobalContext = createContext();

// Session storage keys
const STORAGE_KEYS = {
  SELECTED_ORGANIZATION: 'deviceMapping_selectedOrganization',
  THEME_MODE: 'deviceMapping_themeMode',
  SIDEBAR_OPEN: 'deviceMapping_sidebarOpen',
};

// Initial state
const initialState = {
  selectedOrganization: null,
  themeMode: 'light', // 'light', 'dark', 'system' - default to 'light' for better UX
  sidebarOpen: true, // Sidebar expanded/collapsed state
  isLoading: true, // Add loading state to know when data is loaded from storage
  pendingPinHighlight: null, // { mapId, pinId, pinData } - used to highlight a pin when navigating from sidebar
};

// Action types
const ACTION_TYPES = {
  SET_SELECTED_ORGANIZATION: 'SET_SELECTED_ORGANIZATION',
  SET_THEME_MODE: 'SET_THEME_MODE',
  SET_SIDEBAR_OPEN: 'SET_SIDEBAR_OPEN',
  LOAD_FROM_STORAGE: 'LOAD_FROM_STORAGE',
  SET_LOADING: 'SET_LOADING',
  SET_PENDING_PIN_HIGHLIGHT: 'SET_PENDING_PIN_HIGHLIGHT',
};

// Reducer
function globalReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_SELECTED_ORGANIZATION:
      return {
        ...state,
        selectedOrganization: action.payload,
      };
    case ACTION_TYPES.SET_THEME_MODE:
      return {
        ...state,
        themeMode: action.payload,
      };
    case ACTION_TYPES.SET_SIDEBAR_OPEN:
      return {
        ...state,
        sidebarOpen: action.payload,
      };
    case ACTION_TYPES.LOAD_FROM_STORAGE:
      return {
        ...state,
        ...action.payload,
        isLoading: false, // Mark as loaded when we finish loading from storage
      };
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case ACTION_TYPES.SET_PENDING_PIN_HIGHLIGHT:
      return {
        ...state,
        pendingPinHighlight: action.payload,
      };
    default:
      return state;
  }
}

// Storage utilities
const storage = {
  get: key => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn('Failed to get item from sessionStorage:', error);
      return null;
    }
  },
  set: (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('Failed to set item in sessionStorage:', error);
    }
  },
  remove: key => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove item from sessionStorage:', error);
    }
  },
};

// Context provider
export function GlobalContextProvider({ children }) {
  const [state, dispatch] = useReducer(globalReducer, initialState);
  const { isFeatureEnabled, isLoading: featureFlagsLoading } =
    useFeatureFlags();

  // Track previous organization value to detect changes
  const previousOrganizationRef = useRef(null);

  // Store organization change callbacks
  const organizationChangeCallbacksRef = useRef(new Set());

  // Load data from sessionStorage on mount
  useEffect(() => {
    // Wait for feature flags to load before determining theme mode
    if (featureFlagsLoading) {
      return;
    }

    const savedOrganization = storage.get(STORAGE_KEYS.SELECTED_ORGANIZATION);
    const savedThemeMode = storage.get(STORAGE_KEYS.THEME_MODE);
    const savedSidebarOpen = storage.get(STORAGE_KEYS.SIDEBAR_OPEN);

    // If theme toggle is disabled, force theme to 'light'
    const themeToggleEnabled = isFeatureEnabled(
      FEATURE_FLAGS.ENABLE_THEME_TOGGLE
    );
    let finalThemeMode;
    if (!themeToggleEnabled) {
      // Feature flag disabled: always use 'light', ignore saved theme
      finalThemeMode = 'light';
    } else {
      // Feature flag enabled: use saved theme or default
      // If saved theme is 'system', allow it (will be handled by ThemeSync)
      finalThemeMode = savedThemeMode || initialState.themeMode;
    }

    dispatch({
      type: ACTION_TYPES.LOAD_FROM_STORAGE,
      payload: {
        selectedOrganization: savedOrganization,
        themeMode: finalThemeMode,
        sidebarOpen:
          savedSidebarOpen !== null
            ? savedSidebarOpen
            : initialState.sidebarOpen,
      },
    });

    // Set initial previous organization after loading from storage
    previousOrganizationRef.current = savedOrganization;
  }, [featureFlagsLoading, isFeatureEnabled]);

  // Watch for feature flag changes and update theme accordingly
  useEffect(() => {
    // Wait for feature flags to load and initial state to load
    if (featureFlagsLoading || state.isLoading) {
      return;
    }

    const themeToggleEnabled = isFeatureEnabled(
      FEATURE_FLAGS.ENABLE_THEME_TOGGLE
    );

    // If theme toggle is disabled, force theme to 'light'
    if (!themeToggleEnabled && state.themeMode !== 'light') {
      dispatch({
        type: ACTION_TYPES.SET_THEME_MODE,
        payload: 'light',
      });
      // Persist to sessionStorage
      storage.set(STORAGE_KEYS.THEME_MODE, 'light');
    }
  }, [
    featureFlagsLoading,
    state.isLoading,
    state.themeMode,
    isFeatureEnabled,
    dispatch,
  ]);

  // Detect organization changes and notify callbacks
  useEffect(() => {
    const currentOrgId = state.selectedOrganization?.id;
    const previousOrgId = previousOrganizationRef.current?.id;

    // Only trigger callbacks if organization actually changed (and we're not in initial load)
    if (
      !state.isLoading &&
      currentOrgId !== previousOrgId &&
      previousOrganizationRef.current !== null
    ) {
      // Notify all registered callbacks
      organizationChangeCallbacksRef.current.forEach(callback => {
        try {
          callback(state.selectedOrganization, previousOrganizationRef.current);
        } catch (error) {
          console.error('Error in organization change callback:', error);
        }
      });
    }

    // Update previous organization reference
    previousOrganizationRef.current = state.selectedOrganization;
  }, [state.selectedOrganization, state.isLoading]);

  // Register a callback for organization changes
  const registerOrganizationChangeCallback = useCallback(callback => {
    organizationChangeCallbacksRef.current.add(callback);

    // Return unregister function
    return () => {
      organizationChangeCallbacksRef.current.delete(callback);
    };
  }, []);

  // Actions
  const setSelectedOrganization = organization => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_ORGANIZATION,
      payload: organization,
    });

    // Persist to sessionStorage
    if (organization) {
      storage.set(STORAGE_KEYS.SELECTED_ORGANIZATION, organization);
    } else {
      storage.remove(STORAGE_KEYS.SELECTED_ORGANIZATION);
    }
  };

  const setThemeMode = mode => {
    // If theme toggle is disabled, only allow 'light' theme
    const themeToggleEnabled = isFeatureEnabled(
      FEATURE_FLAGS.ENABLE_THEME_TOGGLE
    );
    const finalMode = themeToggleEnabled ? mode : 'light';

    dispatch({
      type: ACTION_TYPES.SET_THEME_MODE,
      payload: finalMode,
    });

    // Persist to sessionStorage
    storage.set(STORAGE_KEYS.THEME_MODE, finalMode);
  };

  const setSidebarOpen = isOpen => {
    dispatch({
      type: ACTION_TYPES.SET_SIDEBAR_OPEN,
      payload: isOpen,
    });

    // Persist to sessionStorage
    storage.set(STORAGE_KEYS.SIDEBAR_OPEN, isOpen);
  };

  // Clear all stored data (useful for logout)
  const clearStoredData = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      storage.remove(key);
    });

    dispatch({
      type: ACTION_TYPES.LOAD_FROM_STORAGE,
      payload: initialState,
    });
  };

  // Set pending pin highlight (used when clicking a pin in the sidebar)
  const setPendingPinHighlight = pinData => {
    dispatch({
      type: ACTION_TYPES.SET_PENDING_PIN_HIGHLIGHT,
      payload: pinData,
    });
  };

  // Clear pending pin highlight (called after MapViewer handles it)
  const clearPendingPinHighlight = () => {
    dispatch({
      type: ACTION_TYPES.SET_PENDING_PIN_HIGHLIGHT,
      payload: null,
    });
  };

  const value = {
    // State
    selectedOrganization: state.selectedOrganization,
    themeMode: state.themeMode,
    sidebarOpen: state.sidebarOpen,
    isLoading: state.isLoading,
    pendingPinHighlight: state.pendingPinHighlight,

    // Actions
    setSelectedOrganization,
    setThemeMode,
    setSidebarOpen,
    clearStoredData,
    setPendingPinHighlight,
    clearPendingPinHighlight,

    // Organization change callback registration
    registerOrganizationChangeCallback,
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

// Custom hook to use the context
export function useGlobalContext() {
  const context = useContext(GlobalContext);

  if (context === undefined) {
    throw new Error(
      'useGlobalContext must be used within a GlobalContextProvider'
    );
  }

  return context;
}

// Custom hook to automatically refetch data when organization changes
export function useOrganizationChange(callback, deps = []) {
  const { registerOrganizationChangeCallback } = useGlobalContext();

  useEffect(() => {
    const unregister = registerOrganizationChangeCallback(callback);
    return unregister;
  }, [registerOrganizationChangeCallback, ...deps]);
}

export default GlobalContext;
